import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import GetAppIcon from '@material-ui/icons/GetApp'
import { makeStyles } from '@material-ui/core/styles'
import { toast } from 'react-toastify'

import {
  MaxDownloadReportResponse,
  MaxReportListItem,
} from '../../services/max-report.service'

type ReportsListModalProps = {
  open: boolean
  conversationId: string
  reports: MaxReportListItem[]
  hasCachedReports: boolean
  isLoading: boolean
  error: string | null
  onClose: () => void
  onFetchReports: (conversationId: string) => Promise<MaxReportListItem[]>
  onDownloadReport: (reportId: string, tenantId?: string) => Promise<MaxDownloadReportResponse>
}

const POLL_INTERVAL_MS = 1000
const MAX_CONSECUTIVE_IDENTICAL_ERRORS = 15

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 'min(96vw, 1152px)',
    maxWidth: 'none',
    borderRadius: 24,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-elevated, #fff)',
    boxShadow: '0 24px 64px rgba(15, 23, 42, 0.26)',
  },
  titleRoot: {
    padding: theme.spacing(2.5, 3),
    borderBottom: '1px solid var(--ds-border-default, #d7dae4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
  },
  closeButton: {
    padding: theme.spacing(0.5),
    borderRadius: 8,
    color: 'var(--ds-fg-secondary, #535b6c)',
    '&:hover': {
      background: 'var(--ds-bg-subtle, #f1f2f7)',
    },
  },
  content: {
    padding: theme.spacing(2.5, 3),
  },
  tableShell: {
    overflow: 'hidden',
    borderRadius: 16,
    border: '1px solid var(--ds-border-default, #d7dae4)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headRow: {
    background: 'var(--ds-bg-subtle, #f1f2f7)',
  },
  headCell: {
    borderBottom: '1px solid var(--ds-border-default, #d7dae4)',
    padding: theme.spacing(1.5, 2),
    color: 'var(--ds-fg-secondary, #535b6c)',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  bodyRow: {
    borderTop: '1px solid var(--ds-border-default, #d7dae4)',
  },
  bodyCell: {
    padding: theme.spacing(1.5, 2),
    color: 'var(--ds-fg-primary, #1f2430)',
    fontSize: '0.875rem',
    verticalAlign: 'middle',
  },
  emptyCell: {
    padding: theme.spacing(3, 2),
    textAlign: 'center',
    color: 'var(--ds-fg-secondary, #535b6c)',
  },
  errorCell: {
    padding: theme.spacing(3, 2),
    textAlign: 'center',
    color: '#ef4444',
  },
  timestamp: {
    fontWeight: 500,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: theme.spacing(0.5, 1),
    fontSize: '0.75rem',
    lineHeight: 1.2,
    fontWeight: 600,
  },
  statusDone: {
    background: '#d1fae5',
    color: '#047857',
  },
  statusError: {
    background: '#fee2e2',
    color: '#b91c1c',
  },
  statusProgress: {
    background: '#fef3c7',
    color: '#b45309',
  },
  actionButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    borderRadius: 8,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'transparent',
    padding: theme.spacing(0.75, 1.25),
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    cursor: 'pointer',
    transition: 'all 160ms ease',
    '&:hover': {
      background: 'var(--ds-bg-subtle, #f1f2f7)',
    },
  },
  actionButtonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  actionIcon: {
    fontSize: 16,
  },
}))

const hasInProgressReports = (reports: MaxReportListItem[]): boolean =>
  reports.some((report) => report.reportStatus === 'IN_PROGRESS')

const inferExtension = (s3Key: string): string => {
  if (!s3Key) return ''
  const part = s3Key.split('.').pop()
  return part ? part.toLowerCase() : ''
}

const formatTimestamp = (timestamp: string): string => {
  if (!timestamp) return '-'

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(timestamp))
  } catch {
    return '-'
  }
}

const backoffDelay = (attempt: number): number => {
  const step = Math.min(attempt, 6)
  return POLL_INTERVAL_MS * 2 ** step
}

const triggerDownload = (url: string, extension: string) => {
  if (extension === 'pdf') {
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }

  const link = document.createElement('a')
  link.href = url
  link.rel = 'noopener noreferrer'
  link.download = ''
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const ReportsListModal = ({
  open,
  conversationId,
  reports,
  hasCachedReports,
  isLoading,
  error,
  onClose,
  onFetchReports,
  onDownloadReport,
}: ReportsListModalProps) => {
  const classes = useStyles()
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollReportsRef = useRef<(() => Promise<void>) | null>(null)
  const lastErrorMessageRef = useRef<string | null>(null)
  const [consecutiveErrors, setConsecutiveErrors] = useState(0)
  const [lastErrorMessage, setLastErrorMessage] = useState<string | null>(null)
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null)

  const showLoadingState = !hasCachedReports && isLoading
  const tableError = hasCachedReports ? null : error

  const clearPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
  }, [])

  const schedulePoll = useCallback(
    (delayMs: number) => {
      clearPolling()
      pollingTimerRef.current = setTimeout(() => {
        void pollReportsRef.current?.()
      }, delayMs)
    },
    [clearPolling]
  )

  const pollReports = useCallback(async (): Promise<void> => {
    if (!open) return

    if (document.visibilityState !== 'visible') {
      schedulePoll(POLL_INTERVAL_MS)
      return
    }

    try {
      const nextReports = await onFetchReports(conversationId)
      setConsecutiveErrors(0)
      lastErrorMessageRef.current = null
      setLastErrorMessage(null)

      if (hasInProgressReports(nextReports)) {
        schedulePoll(POLL_INTERVAL_MS)
      } else {
        clearPolling()
      }
    } catch (fetchError) {
      const errorMessage =
        fetchError instanceof Error ? fetchError.message : 'Failed to fetch reports'

      setConsecutiveErrors((previous) => {
        const next = lastErrorMessageRef.current === errorMessage ? previous + 1 : 1

        if (next >= MAX_CONSECUTIVE_IDENTICAL_ERRORS) {
          clearPolling()
          toast.error(errorMessage)
          return next
        }

        schedulePoll(backoffDelay(next))
        return next
      })
      lastErrorMessageRef.current = errorMessage
      setLastErrorMessage(errorMessage)
    }
  }, [clearPolling, conversationId, onFetchReports, open, schedulePoll])

  useEffect(() => {
    pollReportsRef.current = pollReports
  }, [pollReports])

  useEffect(() => {
    if (open) {
      void pollReports()
      return
    }

    clearPolling()
  }, [clearPolling, open, pollReports])

  useEffect(() => {
    if (open) {
      setConsecutiveErrors(0)
      lastErrorMessageRef.current = null
      setLastErrorMessage(null)
      void pollReports()
    }
  }, [conversationId, open, pollReports])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!open) return
      if (document.visibilityState === 'visible') {
        void pollReports()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearPolling()
    }
  }, [clearPolling, open, pollReports])

  const handleDownload = async (reportId: string, reportS3Key: string, tenantId: string) => {
    try {
      setDownloadingReportId(reportId)
      const response = await onDownloadReport(reportId, tenantId || undefined)
      triggerDownload(response.downloadUrl, inferExtension(reportS3Key))
    } catch (downloadError) {
      toast.error(downloadError instanceof Error ? downloadError.message : 'Something went wrong')
    } finally {
      setDownloadingReportId(null)
    }
  }

  const rows = useMemo(() => reports, [reports])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography className={classes.titleRoot}>
        <Typography className={classes.title}>Reports</Typography>
        <IconButton
          aria-label="Close modal"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <div className={classes.tableShell}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.headRow}>
                <TableCell className={classes.headCell}>Created</TableCell>
                <TableCell className={classes.headCell}>Type</TableCell>
                <TableCell className={classes.headCell}>Status</TableCell>
                <TableCell className={classes.headCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showLoadingState ? (
                <TableRow className={classes.bodyRow}>
                  <TableCell className={classes.emptyCell} colSpan={4}>
                    Loading reports...
                  </TableCell>
                </TableRow>
              ) : tableError ? (
                <TableRow className={classes.bodyRow}>
                  <TableCell className={classes.errorCell} colSpan={4}>
                    {tableError}
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow className={classes.bodyRow}>
                  <TableCell className={classes.emptyCell} colSpan={4}>
                    No reports generated yet.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const isDone = row.reportStatus === 'DONE'
                  const isDownloading = downloadingReportId === row.reportId
                  return (
                    <TableRow key={row.reportId} className={classes.bodyRow}>
                      <TableCell className={classes.bodyCell}>
                        <span className={classes.timestamp}>
                          {formatTimestamp(row.creationTime)}
                        </span>
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        {row.reportTypeText || row.reportType || '-'}
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        <span
                          className={`${classes.statusBadge} ${
                            row.reportStatus === 'DONE'
                              ? classes.statusDone
                              : row.reportStatus === 'ERROR'
                              ? classes.statusError
                              : classes.statusProgress
                          }`}
                        >
                          {row.reportStatus || '-'}
                        </span>
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        <button
                          type="button"
                          className={`${classes.actionButton} ${
                            !isDone || isDownloading ? classes.actionButtonDisabled : ''
                          }`}
                          disabled={!isDone || isDownloading}
                          onClick={() => {
                            void handleDownload(
                              row.reportId,
                              row.reportS3Key || '',
                              String(row.tenantId || '')
                            )
                          }}
                        >
                          <GetAppIcon className={classes.actionIcon} />
                          {isDownloading ? 'Loading...' : 'Download'}
                        </button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ReportsListModal
